import React, { useState } from "react";

const DEMO_FIRM_ID = "hannah-financial-demo";

const DemoLogin: React.FC = () => {
  const [redirecting, setRedirecting] = useState<string | null>(null);

  const enterDemo = (role: string) => {
    setRedirecting(role);

    // Normalize role to match the rest of your app conventions
    const normalizedRole =
      role === "TaxPro" ? "Tax Pro" : role === "Client" ? "CLIENT" : role;

    const isClient = normalizedRole === "CLIENT";

    // ✅ Single source of truth (JSON) used by AuthContext
    sessionStorage.setItem(
      "FILERSHUB_DEMO_SESSION",
      JSON.stringify({ role: normalizedRole, isClient })
    );

    // ✅ Convenience keys (AuthContext reads these too)
    sessionStorage.setItem("FILERSHUB_DEMO_ROLE", normalizedRole);

    // ✅ Role-specific UID so profile can vary by role if you want
    const uid =
      normalizedRole === "FirmOwner"
        ? "demo_FirmOwner"
        : normalizedRole === "Manager"
        ? "demo_Manager"
        : normalizedRole === "Tax Pro"
        ? "demo_TaxPro"
        : "demo_CLIENT";

    sessionStorage.setItem("FILERSHUB_DEMO_UID", uid);

    // ✅ Tenant context
    sessionStorage.setItem("FILERSHUB_SSO_LOC", DEMO_FIRM_ID);

    // ✅ Go to the tenant-scoped dashboard
    const targetPath = `/location/${DEMO_FIRM_ID}/dashboard`;

    setTimeout(() => {
      window.location.href = targetPath;
    }, 200);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6 font-sans overflow-hidden relative">
      {/* Background Watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden opacity-[0.02] -z-0">
        <span className="text-[25vw] font-black uppercase tracking-tighter -rotate-12 whitespace-nowrap text-white">
          FILERS HUB
        </span>
      </div>

      {/* Visual background elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-emerald-500 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-brand-500 rounded-full blur-[150px]"></div>
      </div>

      <div className="w-full max-w-4xl z-10 animate-fade-in-up">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
            <span className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.3em]">
              Developer Access Terminal
            </span>
          </div>
          <h1 className="text-5xl font-black text-white tracking-tight mb-4">
            Environment Simulation
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto font-medium">
            Select a verified identity to bypass standard authentication and enter the Hannah Financial sandbox.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              role: "FirmOwner",
              title: "Firm Owner",
              desc: "Complete administrative authority over firm configuration and staff.",
              icon: "🏢",
              color: "border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10",
              accent: "bg-emerald-500",
            },
            {
              role: "Manager",
              title: "Firm Manager",
              desc: "High-level oversight of all client engagements and standard documents.",
              icon: "🛡️",
              color: "border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10",
              accent: "bg-blue-500",
            },
            {
              role: "TaxPro",
              title: "Tax Professional",
              desc: "Direct management of assigned client tax returns and messaging.",
              icon: "💼",
              color: "border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10",
              accent: "bg-purple-500",
            },
            {
              role: "Client",
              title: "Portal Client",
              desc: "End-user view for document uploads, signatures, and return tracking.",
              icon: "👤",
              color: "border-brand-500/30 bg-brand-500/5 hover:bg-brand-500/10",
              accent: "bg-brand-500",
            },
          ].map((item) => (
            <button
              key={item.role}
              disabled={!!redirecting}
              onClick={() => enterDemo(item.role)}
              className={`text-left p-8 rounded-[2.5rem] border transition-all duration-300 group hover:scale-[1.02] flex flex-col justify-between h-full relative overflow-hidden ${item.color} ${
                redirecting === item.role ? "ring-2 ring-white/50" : ""
              }`}
            >
              {redirecting === item.role && (
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm flex items-center justify-center z-20">
                  <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
              )}

              <div>
                <div className="flex justify-between items-start mb-6">
                  <span className="text-4xl">{item.icon}</span>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all ${item.accent}`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed font-medium">{item.desc}</p>
              </div>

              <div className="mt-8 flex items-center gap-2">
                <div
                  className={`w-1.5 h-1.5 rounded-full ${item.accent} ${
                    redirecting === item.role ? "animate-ping" : ""
                  }`}
                ></div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
                  {redirecting === item.role ? "Securing Identity..." : "Establish Session"}
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-16 pt-12 border-t border-slate-800 text-center">
          <button
            onClick={() => (window.location.href = "/")}
            className="text-slate-500 hover:text-white transition-colors text-xs font-black uppercase tracking-[0.3em]"
          >
            ← Return to Production Gateway
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoLogin;
